import type { ButtonHTMLAttributes, InputHTMLAttributes, ReactNode, TextareaHTMLAttributes } from 'react';
import { useId } from 'react';
import { Icon, type IconName } from './Icon';

const cx = (...a: (string | false | null | undefined)[]) => a.filter(Boolean).join(' ');

/* ───────── Button ───────── */
export interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: 'primary' | 'black' | 'ghost' | 'danger' | 'soft';
  size?: 'md' | 'sm';
  /** Đang xử lý: khoá nút (chống bấm đúp) và hiện spinner. */
  loading?: boolean;
}
export function Button({ variant = 'primary', size = 'md', loading, disabled, className, children, type = 'button', ...rest }: ButtonProps) {
  return (
    <button
      type={type}
      disabled={disabled || loading}
      aria-busy={loading || undefined}
      className={cx('g-btn', variant !== 'primary' && `g-btn--${variant}`, size === 'sm' && 'g-btn--sm', className)}
      {...rest}
    >
      {loading ? (<><span style={{ opacity: 0, display: 'inline-flex', alignItems: 'center', gap: 8 }}>{children}</span><span className="g-spin" aria-hidden="true" /></>) : children}
    </button>
  );
}

export function IconButton({ icon, label, flat, size = 22, ...rest }: { icon: IconName; label: string; flat?: boolean; size?: number } & ButtonHTMLAttributes<HTMLButtonElement>) {
  return (
    <button type="button" aria-label={label} className={cx('g-iconbtn', flat && 'g-iconbtn--flat')} {...rest}>
      <Icon name={icon} size={size} />
    </button>
  );
}

/* ───────── Card / Pill / Chip ───────── */
export function Card({ children, className, style }: { children: ReactNode; className?: string; style?: React.CSSProperties }) {
  return <div className={cx('g-card', className)} style={style}>{children}</div>;
}
export function Pill({ tone = 'neutral', children }: { tone?: 'neutral' | 'ok' | 'warn' | 'bad'; children: ReactNode }) {
  return <span className={cx('g-pill', tone !== 'neutral' && `g-pill--${tone}`)}>{children}</span>;
}
export interface ChipOption<T extends string> { value: T; label: string }
export function ChipGroup<T extends string>({ options, value, onChange, label }: { options: ChipOption<T>[]; value: T; onChange: (v: T) => void; label: string }) {
  return (
    <div className="g-chips" role="group" aria-label={label}>
      {options.map((o) => (
        <button key={o.value} type="button" className="g-chip" aria-pressed={o.value === value} onClick={() => onChange(o.value)}>{o.label}</button>
      ))}
    </div>
  );
}

/* ───────── Banner ───────── */
export function Banner({ tone = 'info', children, actionLabel, onAction }: { tone?: 'err' | 'warn' | 'info'; children: ReactNode; actionLabel?: string; onAction?: () => void }) {
  return (
    <div className={`g-banner g-banner--${tone}`} role={tone === 'info' ? 'status' : 'alert'}>
      <span>{children}</span>
      {actionLabel && <button type="button" className="g-banner__action" onClick={onAction}>{actionLabel}</button>}
    </div>
  );
}

/* ───────── Field ───────── */
interface FieldCommon { label: string; error?: string | null }
export function TextField({ label, error, ...rest }: FieldCommon & InputHTMLAttributes<HTMLInputElement>) {
  const id = useId();
  return (
    <div className="g-field">
      <label className="g-field__label" htmlFor={id}>{label}</label>
      <input id={id} className="g-input" aria-invalid={!!error || undefined} aria-describedby={error ? `${id}-e` : undefined} {...rest} />
      {error && <div id={`${id}-e`} className="g-field__error" role="alert">{error}</div>}
    </div>
  );
}
export function TextArea({ label, error, ...rest }: FieldCommon & TextareaHTMLAttributes<HTMLTextAreaElement>) {
  const id = useId();
  return (
    <div className="g-field">
      <label className="g-field__label" htmlFor={id}>{label}</label>
      <textarea id={id} className="g-input" aria-invalid={!!error || undefined} aria-describedby={error ? `${id}-e` : undefined} {...rest} />
      {error && <div id={`${id}-e`} className="g-field__error" role="alert">{error}</div>}
    </div>
  );
}

/* ───────── Skeleton / Empty ───────── */
export function Skeleton({ width = '100%', height = 16, radius, style }: { width?: number | string; height?: number; radius?: number; style?: React.CSSProperties }) {
  return <div className="g-skel" aria-hidden="true" style={{ width, height, borderRadius: radius, ...style }} />;
}
export function CardSkeleton({ count = 3 }: { count?: number }) {
  return (
    <div role="status" aria-label="Đang tải" aria-busy="true">
      <div className="g-loading-label"><span className="g-loading-spinner" aria-hidden="true"/>Đang tải trang…</div>
      {Array.from({ length: count }, (_, i) => (
        <Card key={i}>
          <Skeleton width="55%" style={{ marginBottom: 14 }} />
          <Skeleton height={18} style={{ marginBottom: 10 }} />
          <Skeleton height={18} width="80%" style={{ marginBottom: 16 }} />
          <Skeleton height={34} width="40%" />
        </Card>
      ))}
    </div>
  );
}
export function EmptyState({ icon = 'clock', title, description, action }: { icon?: IconName; title: string; description?: string; action?: ReactNode }) {
  return (
    <div className="g-empty">
      <div className="g-empty__icon"><Icon name={icon} size={40} /></div>
      <strong style={{ color: 'var(--g-ink)' }}>{title}</strong>
      {description && <p style={{ margin: '6px 0 0' }}>{description}</p>}
      {action && <div style={{ marginTop: 16 }}>{action}</div>}
    </div>
  );
}

/** Khối lỗi tải dữ liệu có nút "Thử lại". */
export function ErrorBlock({ message, onRetry }: { message: string; onRetry?: () => void }) {
  return <Banner tone="err" actionLabel={onRetry ? 'Thử lại' : undefined} onAction={onRetry}>{message}</Banner>;
}

/* ───────── TopBar / Stepper / KV ───────── */
export function TopBar({ title, onBack, right }: { title: string; onBack?: () => void; right?: ReactNode }) {
  return (
    <div className="g-topbar">
      {onBack ? <IconButton icon="back" label="Quay lại" onClick={onBack} /> : <span className="g-topbar__spacer" />}
      <h2 className="g-topbar__title">{title}</h2>
      {right ?? <span className="g-topbar__spacer" />}
    </div>
  );
}
export function Stepper({ steps, current, label }: { steps: string[]; current: number; label: string }) {
  return (
    <ol className="g-stepper" aria-label={label}>
      {steps.map((s, i) => (
        <li key={s} className={cx(i < current && 'is-done', i === current && 'is-current')} aria-current={i === current ? 'step' : undefined}>{s}</li>
      ))}
    </ol>
  );
}
export function KeyValue({ k, children }: { k: string; children: ReactNode }) {
  return <div className="g-kv"><span>{k}</span><b>{children}</b></div>;
}
